<template>
	<div class="regular-shop-item-container" @click="buyPackage">
		<ToastMessage
			class="regular-shop-item__toast-message"
			width="auto"
			:translatedText="translate('ui.try_again_later')"
			:showMessage="isToastVisible"
			v-if="shouldShowTryAgainToast"
		/>
		<ToastMessage
			class="regular-shop-item__toast-message-buy"
			width="auto"
			:translatedText="translate('ui.purchase_successful')"
			:showMessage="isBuyToastVisible"
			:hasAnimation="true"
		/>
		<div v-if="contentData" class="regular-shop-item">
			<div
				v-if="contentData.value && resourceKey"
				class="regular-shop-item__header"
			>
				<BoxHeaderBackground slug="regular-shop-item-header" />
				<div class="regular-shop-item__header-text-container">
					<ShrinkText
						:text="translate(`resource.${resourceKey}`)"
						:fontSize="20"
						:maxLineLimit="1"
						hasSameLineHeight
					/>
					<div class="resource-display">
						<span class="resource-display__label">
							{{ translate("ui.owned") }}:
						</span>
						<span class="resource-display__value">
							{{ formatNumber(resourceValue) }}
						</span>
					</div>
				</div>
			</div>

			<div class="regular-shop-item__content">
				<div class="regular-shop-item__content-horizontal">
					<div class="regular-shop-item__icon-wrapper">
						<AppMainIcon
							v-if="contentData.value && resourceKey"
							:iconName="resourceKey"
							:iconSize="40"
						/>
						<div
							v-else
							:class="`icon-${contentData.iconName}`"
							:iconSize="40"
						/>
					</div>
					<div class="regular-shop-item__vertical-values">
						<AppPrice
							v-for="[key, value] in offerEntries"
							:key="key"
							:amount="value"
							:currency="key"
							:resourceChangeSign="
								ResourceChangeHighlight.POSITIVE
							"
							:resourceCheck="false"
							:fontSize="30"
							:iconSize="20"
						/>
						<p
							v-if="contentData.header"
							class="regular-shop-item__vertical-values__header"
						>
							{{ translate(contentData.header) }}
						</p>
						<p v-if="contentData.description">
							{{
								replacePlaceholder(
									translate(contentData.description),
									"{number}",
									contentData.replaceValue,
								)
							}}
						</p>
						<div
							v-if="contentData.ribbonInfo"
							class="ribbon-label-info"
						>
							<span>
								{{
									replacePlaceholder(
										translate(contentData.ribbonInfo),
										"{2to4}",
										3,
									)
								}}
							</span>
						</div>
						<Timer
							v-if="contentData.timeLimit"
							:time="contentData.timeLimit"
							:hasIcon="false"
						/>
					</div>
				</div>
			</div>

			<div
				class="regular-shop-item__footer"
				:class="{
					['regular-shop-item__footer--purchased']:
						contentData.isBought,
					['regular-shop-item__footer__condition-fail']:
						isConditionFailed,
				}"
			>
				<template v-if="isConditionFailed">
					<BoxHeaderBackground
						slug="regular-shop-item-footer"
						verticallyFlipped
					/>
					<span
						class="regular-shop-item__footer__condition-fail--text"
					>
						{{
							translate(contentData?.dynamicDescription?.() || "")
						}}
					</span>
				</template>
				<template v-else>
					<BoxHeaderBackground
						slug="regular-shop-item-footer"
						verticallyFlipped
					/>
					<div class="regular-shop-item__footer-text-container">
						<span
							v-if="contentData.limit"
							class="regular-shop-item__footer-text-container--left"
						>
							{{
								`${translate("ui.limit")} ${contentData.limit.current}/${contentData.limit.max}`
							}}
						</span>
						<div
							class="regular-shop-item__footer-text-container--right"
							:class="contentData.isBought ? 'center' : 'right'"
						>
							<span
								class="purchased-label"
								v-if="contentData.isBought"
							>
								{{ translate("ui.purchased") }}
							</span>
							<div class="amount" v-else-if="contentData.price">
								<span class="price-label">
									{{ translate("ui.price") }}:
								</span>
								<AppPrice
									v-for="(value, key) in contentData.price"
									:key="key"
									:amount="value ?? 0"
									:currency="key"
									:fontSize="20"
									:iconSize="16"
								/>
							</div>
							<div class="amount" v-else>
								<span class="price-label">
									{{ translate("ui.price") }}:
								</span>
								<span class="amount-free">
									{{ translate("ui.free") }}
								</span>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
		<div class="regular-shop-item-decoration">
			<GoldenCorner top left />
			<GoldenCorner top right />
			<GoldenCorner bottom left />
			<GoldenCorner bottom right />
		</div>
		<AppNotification
			v-if="showNotification"
			value="!"
			:type="StatusTypes.WARNING"
		/>
	</div>
</template>

<script setup lang="ts">
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppNotification from "@/components/AppNotification.vue";
import AppPrice from "@/components/AppPrice.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import BoxHeaderBackground from "@/components/SVG/BoxHeaderBackground.vue";
import GoldenCorner from "@/components/SVG/GoldenCorner.vue";
import Timer from "@/components/Timer.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import type { Offer } from "@/game/config/gameCurrencyOffers";
import type { ResourceType } from "@/game/entities/config/resources";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useResourcesStore from "@/store/useResourcesStore";
import type { ConversionOffer } from "@/types/shop";
import { OfferType, ResourceChangeHighlight, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, onUnmounted, ref } from "vue";

interface Props {
	contentData?: Offer | ConversionOffer;
	showNotification?: boolean;
}

const { contentData, showNotification = false } = defineProps<Props>();
const emit = defineEmits(["buyPackage"]);

const emitter = markRaw(useEmitter());
const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const isToastVisible = ref<boolean>(false);
const isBuyToastVisible = ref<boolean>(false);

const resourceKey = computed((): ResourceType | null =>
	contentData?.value
		? (Object.keys(contentData.value)[0] as ResourceType)
		: null,
);

const resourceValue = computed((): number =>
	resourceKey.value ? resources[resourceKey.value].value : 0,
);

const offerEntries = computed(
	(): Array<[ResourceType, number]> =>
		Object.entries(contentData?.value ?? {}) as Array<
			[ResourceType, number]
		>,
);

const isConditionFailed = computed(
	(): boolean =>
		contentData?.type === OfferType.ACTION_FOR_GEMS &&
		!!(
			!contentData?.condition?.() ||
			contentData?.conditionForNotAvailability?.()
		),
);

const shouldShowTryAgainToast = computed(
	(): boolean =>
		!!(
			contentData?.timeLimit ||
			(!contentData?.price &&
				contentData?.limit &&
				contentData.limit.current >= contentData.limit.max)
		) && !isBuyToastVisible.value,
);

const buyPackage = (): void => {
	if (isConditionFailed.value) return;

	if (contentData?.timeLimit || !contentData?.condition?.()) {
		isToastVisible.value = true;
		setTimeout((): boolean => (isToastVisible.value = false), 800);
	}
	emit("buyPackage", contentData);
};

const buyMessageListener = (offerData: {
	offerId: number;
	offerType: OfferType;
}): void => {
	if (
		contentData?.id === offerData.offerId &&
		contentData?.type === offerData.offerType
	) {
		isBuyToastVisible.value = true;
		setTimeout((): boolean => (isBuyToastVisible.value = false), 1000);
	}
};

onMounted((): void =>
	emitter.on(EmitterEvents.ShopItemPurchased, buyMessageListener),
);

onUnmounted((): void =>
	emitter.off(EmitterEvents.ShopItemPurchased, buyMessageListener),
);
</script>

<style lang="scss" scoped>
.regular-shop-item {
	position: relative;
	box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
	border-width: 0.05rem;
	border-style: solid;
	border-image-slice: 1;
	@include flex-col-center;

	border-image-source: linear-gradient(to top, #c08b26, #ceae56);
	background: linear-gradient(to top, #4a3a1e, #d6a44d);
	width: 100%;
	height: 100%;

	&-container {
		position: relative;
		width: 100%;
		@include clickable-element;
		order: 0; // NOTE: Order is important

		&--promoted {
			order: 0;
		}

		&--subordinate {
			order: 1;
		}

		:deep(.notification) {
			position: absolute;
			top: -0.369rem;
			right: -0.369rem;
			transform: scale(1.2);
			z-index: 1;
		}
	}

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		z-index: 0;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: repeat center;
		@include background(
			url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
			40%,
			center,
			repeat
		);
		width: 100%;
		height: 100%;
		content: "";
	}

	&__toast-message {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		opacity: 0;
		z-index: 1;
		animation: fadeInUp 0.8s ease-in forwards;
		transition: all 0.8s ease-in;

		@keyframes fadeInUp {
			0% {
				opacity: 0;
				margin-top: 0rem;
			}
			20% {
				opacity: 1;
			}
			80% {
				opacity: 1;
			}
			100% {
				opacity: 0;
				margin-top: -0.75rem;
			}
		}
		&-buy {
			position: absolute;
			top: 50%;
			left: 40%;
		}
	}

	.regular-shop-item__header {
		position: relative;
		flex-grow: 0;
		flex-shrink: 0;
		width: 100%;
		height: 2rem;
		// NOTE: z-index cannot be here

		:deep(.box-header-bg) {
			position: relative;
			mix-blend-mode: multiply;
			width: 100%;
			height: 2rem;

			.solid-bg-polygon {
				fill: rgba(92, 65, 18, 0.8) !important;
			}

			.texture-bg-polygon {
				opacity: 0.2 !important;
			}
		}

		&-text-container {
			@include flex-row-start;
			position: absolute;
			top: 0;
			left: 0;
			padding: 0 1.125rem;
			width: 100%;
			height: 100%;

			.text-container {
				flex: 1;
				width: auto;
				@include flex-row-start;
			}

			:deep(.shrink-text) {
				padding-bottom: 0.375rem;
				color: #f4d278;
				font-weight: 800;
				text-align: left;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				text-transform: uppercase;
			}
		}

		.resource-display {
			@include flex-row-start;
			gap: 0.375rem;
			padding-bottom: 0.125rem;
			text-transform: lowercase;
		}
	}

	&__content {
		position: relative;
		flex: 1 1 auto;
		width: 100%;
		min-height: 0;

		&-vertical {
			@include flex-col-center;
			height: 100%;
		}

		&-horizontal {
			@include flex-row-center;
			justify-content: flex-start;
			gap: 1rem;
			padding: 0 1.375rem;
			height: 100%;

			.icon {
				flex-shrink: 0;
				margin: 0.625rem 0;
			}
		}
	}

	&__footer {
		position: relative;
		flex-grow: 0;
		flex-shrink: 0;
		width: 100%;
		height: 2rem;
		// NOTE: z-index cannot be here

		:deep(.box-header-bg) {
			position: relative;
			mix-blend-mode: unset;
			width: 100%;
			height: 2rem;

			.solid-bg-polygon {
				fill: rgba(70, 47, 5, 0.8) !important;
			}

			.texture-bg-polygon {
				opacity: 0.4 !important;
				mix-blend-mode: multiply;
			}
		}

		&--purchased {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: rgba(106, 0, 15, 0.8) !important;
				}
			}
		}

		&-text-container {
			@include flex-row-center;
			position: absolute;
			top: 0;
			left: 0;
			justify-content: space-between;
			padding: 0 1rem 0 1.875rem;
			width: 100%;
			height: 100%;

			span {
				padding-bottom: 0.125rem;
				color: #fff3dd;
				font-weight: 800;
				font-size: 0.75rem;
				text-align: center;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}

			&--right {
				flex: 1 1 auto;
				@include flex-row-center;

				&.right {
					justify-content: flex-end;
				}

				&.center {
					justify-content: center;
				}

				.price-label,
				.purchased-label,
				.amount-free {
					font-weight: 500;
					font-size: 1.25rem;
					text-transform: uppercase;
				}
			}

			.amount {
				@include flex-row-center;
				align-items: baseline;
				gap: 0.5rem;
				&-free {
					font-weight: bold;
					text-transform: uppercase;
				}
			}
		}

		&__condition-fail {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: rgba(106, 0, 15, 0.8) !important;
				}
			}
			&--text {
				@include flex-row-center;
				position: absolute;
				top: 0;
				width: 100%;
				height: 100%;
			}
		}
	}

	&__values {
		flex: 1 1 auto;
		@include flex-col-center;
		gap: 1rem;
		padding-top: 1.5rem;
		padding-bottom: 0.5rem;
		height: 100%;

		.pack {
			&__values {
				@include flex-col-center;
				gap: 0.875rem;
				padding: 0 1.25rem;
				width: 100%;
			}
		}

		.text-button {
			width: 7.5rem;
		}
	}

	&__vertical-values {
		@include flex-col-center;
		flex: 1 1 auto;
		padding-top: 1.125rem;
		padding-bottom: 0.875rem;

		p {
			font-size: 0.75rem;
			line-height: 0.875rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0 rgba(0, 0, 0, 0.6);

			&:first-of-type {
				margin-top: 0.75rem;
			}
		}

		.ribbon-label-info {
			position: relative;
			@include flex-row-center;
			align-items: baseline;
			gap: 0.25rem;
			margin: 0.375rem 0 0.188rem;
			background-image: linear-gradient(
				to right,
				rgba(85, 0, 20, 0) 0%,
				rgba(85, 0, 20, 0.8) 10%,
				rgba(85, 0, 20, 0.8) 90%,
				rgba(85, 0, 20, 0) 100%
			);
			padding: 0 2rem 0.25rem;

			span {
				font-weight: 500;
				font-size: 0.75rem;
			}

			&::before,
			&::after {
				position: absolute;
				left: 0;
				background: linear-gradient(
					to right,
					rgba(207, 0, 50, 0) 5%,
					rgba(207, 0, 50, 0.8) 20%,
					rgba(207, 0, 50, 0.8) 80%,
					rgba(207, 0, 50, 0) 95%
				);
				width: 100%;
				height: 0.05rem;
				content: "";
			}

			&::before {
				top: 0;
			}

			&::after {
				bottom: 0;
			}
		}

		&__header {
			color: #fff3dd;
			font-weight: 800;
			font-size: 1rem;
		}
	}

	&-decoration {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		width: 100%;
		height: 100%;
		pointer-events: none;

		:deep(.golden-corner) {
			width: 1rem;
			height: 1rem;
		}

		:deep(.golden-corner--top-left) {
			position: absolute;
			top: 0;
			left: 0;
		}

		:deep(.golden-corner--top-right) {
			position: absolute;
			top: 0;
			right: 0;
		}

		:deep(.golden-corner--bottom-left) {
			position: absolute;
			bottom: 0;
			left: 0;
		}

		:deep(.golden-corner--bottom-right) {
			position: absolute;
			right: 0;
			bottom: 0;
		}
	}

	&__icon-wrapper {
		margin-top: 0.375rem;
		margin-bottom: 0.25rem;
		@include background(
			url($path-images + "shop/backgrounds/reward-box.avif"),
			contain,
			center,
			no-repeat
		);
		flex-shrink: 0;
		@include flex-row-center;
		filter: drop-shadow(0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6));
		width: 4.313rem;
		height: 4.313rem;
	}
}
</style>
